import { useState, useCallback, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';

const SEO_PAGES = '/cms/seo-pages';

/**
 * The SEO Page Manager API (SEO Master Phase 1 §6).
 *
 * The derived fields - canonicalPath, autoSeoTitle, effectiveSeoTitle, inSitemap, warnings -
 * are computed by the BACKEND and arrive with every row. Nothing is re-derived here on
 * purpose: the dashboard's URL preview and the website's rendered page must come from one
 * implementation, or the screen will confidently preview a URL that does not exist.
 */

/** Every SEO page matching the filters. Returns { data, total, loading, error, refetch }. */
export function useSeoPagesList(params = {}) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters arrive as a fresh object on every render, so depending on the object itself
  // would refetch forever. Serialise it and depend on the string.
  const key = JSON.stringify({
    pageType: params.pageType || undefined,
    publicationStatus: params.publicationStatus || undefined,
    indexStatus: params.indexStatus || undefined,
    search: params.search || undefined,
  });

  // Only the newest request may write state: a fast typist outruns the network, and without
  // this the list settles on whichever response happens to land last.
  const reqId = useRef(0);

  const fetchList = useCallback(async () => {
    const mine = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(SEO_PAGES, {
        params: { ...JSON.parse(key), limit: 500 },
      });
      if (mine !== reqId.current) return;
      setData(Array.isArray(res?.data) ? res.data : []);
      setTotal(res?.meta?.totalItems ?? (Array.isArray(res?.data) ? res.data.length : 0));
    } catch (err) {
      if (mine !== reqId.current) return;
      setError(err?.response?.data?.message || 'Failed to load SEO pages');
      setData([]);
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [key]);

  useEffect(() => { fetchList(); }, [fetchList]);

  return { data, total, loading, error, refetch: fetchList };
}

/** Create, update and delete. Each reports its own loading state for the save button. */
export function useSeoPageMutations() {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const create = useCallback(async (payload) => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.post(SEO_PAGES, payload);
      return data?.data;
    } finally { setSaving(false); }
  }, []);

  const update = useCallback(async (id, payload) => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put(`${SEO_PAGES}/${id}`, payload);
      return data?.data;
    } finally { setSaving(false); }
  }, []);

  const remove = useCallback(async (id) => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`${SEO_PAGES}/${id}`);
    } finally { setDeleting(false); }
  }, []);

  return { create, update, remove, saving, deleting };
}
